<template>
  <div class="container mx-auto max-w-4xl">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold">Notifications</h1>
      <div class="flex items-center space-x-4">
        <button 
          v-if="notifications.some(n => !n.read)"
          @click="markAllAsRead"
          class="btn btn-secondary"
        >
          Mark all as read
        </button>
      </div>
    </div>

    <div class="card divide-y divide-secondary-800">
      <div v-if="isLoading && !notifications.length" class="p-6 text-center text-gray-400">
        Loading notifications...
      </div>
      <div v-else-if="error" class="p-6 text-center">
        <p class="text-red-400">{{ error }}</p>
        <button 
          @click="handleRefresh"
          class="btn btn-primary mt-4"
        >
          Retry
        </button>
      </div>
      <div v-else-if="!notifications.length" class="p-6 text-center text-gray-400">
        No notifications
      </div>
      
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        class="p-6 hover:bg-dark-200 cursor-pointer"
        :class="{ 'bg-dark-200/50': !notification.read }"
        @click="handleNotificationClick(notification)"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <p class="font-medium" :class="{ 'text-white': !notification.read, 'text-gray-300': notification.read }">
              {{ notification.title }}
            </p>
            <p class="text-gray-400 mt-2 whitespace-pre-line">{{ notification.description }}</p>
            <div class="flex items-center space-x-4 mt-2">
              <p class="text-sm text-gray-500">{{ formatDate(notification.createdAt) }}</p>
              <router-link 
                v-if="notification.testRun"
                :to="`/test-execution/${getNumericId(notification.testRunId)}/${notification.testRun.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`"
                class="text-sm text-primary-400 hover:text-primary-300"
                @click.stop
              >
                View Test Run
              </router-link>
            </div>
          </div>
          <div v-if="!notification.read" class="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch } from 'vue';
import { useNotificationsStore } from '@/stores/notificationsStore';
import { useRouter } from 'vue-router';
import { formatDistanceToNow } from 'date-fns';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const notificationsStore = useNotificationsStore();
const authStore = useAuthStore();
let refreshInterval = null;

const { 
  notifications,
  isLoading,
  error,
  fetchAllNotifications,
  markAsRead,
  markAllAsRead: markAllNotificationsAsRead,
  refresh: refreshNotifications
} = notificationsStore;

// Single watcher for auth state
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
  if (!isAuthenticated) {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
    router.push('/login');
  } else {
    // Fetch notifications and start refresh interval
    fetchAllNotifications();
    startRefreshInterval();
  }
}, { immediate: true });

function startRefreshInterval() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  refreshInterval = setInterval(() => {
    if (authStore.isAuthenticated) {
      fetchAllNotifications().catch(err => {
        console.error('NotificationsView: Error in refresh interval:', err);
      });
    }
  }, 30000); // Refresh every 30 seconds
}

onMounted(async () => {
  if (authStore.isAuthenticated) {
    try {
      await fetchAllNotifications();
    } catch (err) {
      console.error('NotificationsView: Error fetching notifications:', err);
    }
  } else {
    router.push('/login');
  }
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});

// Replace the error watcher with a proper reactive watcher
watch(() => notificationsStore.error, (newVal) => {
  if (newVal) {
    console.error('NotificationsView: Error state:', newVal);
  }
}, { immediate: true });

async function handleNotificationClick(notification) {
  if (!notification.read) {
    await markAsRead(notification.id);
  }

  // Handle navigation based on notification type
  if ((notification.type === 'test_run_completed' || notification.type === 'comment_mention') && notification.testRun) {
    const slug = notification.testRun.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    router.push(`/test-execution/${getNumericId(notification.testRunId)}/${slug}`);
  }
}

async function markAllAsRead() {
  await markAllNotificationsAsRead();
}

function formatDate(date) {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

// Function to generate a numeric ID from UUID
const getNumericId = (uuid) => {
  return parseInt(uuid.slice(0, 8), 16);
}

// Enhanced refresh handler using the store's refresh function
async function handleRefresh() {
  try {
    await refreshNotifications();
  } catch (err) {
    console.error('NotificationsView: Error during refresh:', err);
  }
}
</script> 